import * as React from 'react';

import numeral from 'numeral';

import {
  createStyles,
  Grid,
  Theme,
  Typography,
  WithStyles,
  withStyles
} from '@material-ui/core';

import {
  ILieuucationCbsaProperties,
  ILieuucationProperties
} from '@lieuu/core';

import LieuucationTable from '../charts/LieuucationTable';
import { getMortgageCost } from './calculations';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
      [theme.breakpoints.up('lg')]: {
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6)
      }
    },
    grid: {
      width: '100%'
    },
    summary: {
      marginTop: theme.spacing(2)
    }
  });

type IProps = WithStyles<typeof styles> & {
  properties: ILieuucationProperties;
  score: number;
  cbsaProperties: ILieuucationCbsaProperties;
  // countryProperties: ILieuucationCountryProperties;
  // zipProperties: ILieuucationZipProperties;
};

const ExecutiveSummary: React.FC<IProps> = ({
  score,
  classes,
  properties,
  cbsaProperties
  // countryProperties
}) => {
  const scoreString = numeral(score).format('0%');

  const ageRatio =
    properties.marketProfile.typicalAge.latest /
    cbsaProperties.marketProfile.typicalAge.latest;

  const incomeRatio =
    properties.marketProfile.income.household.latest /
    cbsaProperties.marketProfile.income.household.latest;

  const valueRatio =
    properties.housing.homeValue.latest /
    cbsaProperties.housing.homeValue.latest;

  const mortgageCost = getMortgageCost(properties.housing.homeValue.latest);
  const mortgageCostCbsa = getMortgageCost(
    cbsaProperties.housing.homeValue.latest
  );

  return (
    <div className={classes.root}>
      <Grid justify="center" spacing={2} className={classes.grid} container>
        <Grid item xs={12} md={6}>
          <Typography variant="h6">Market Profile</Typography>
          <LieuucationTable
            head={[
              '',
              'Micromarket',
              'City'
              //  'Country'
            ]}
            rows={[
              [
                'Age',
                numeral(properties.marketProfile.typicalAge.latest).format(
                  '0.0'
                ),
                numeral(cbsaProperties.marketProfile.typicalAge.latest).format(
                  '0.0'
                )
                // numeral(
                //   countryProperties.marketProfile.typicalAge.latest
                // ).format('0.0')
              ],
              [
                'Household Income',
                properties.marketProfile.income.household.latest > 0
                  ? numeral(
                      properties.marketProfile.income.household.latest
                    ).format('$0,0a')
                  : 'N/A',
                numeral(
                  cbsaProperties.marketProfile.income.household.latest
                ).format('$0,0a')
                // numeral(
                //   countryProperties.marketProfile.income.household.latest
                // ).format('$0,0a')
              ],
              [
                'Owner Costs',
                properties.housing.monthlyOwnerCostsPercentIncome.latest > 0
                  ? numeral(
                      properties.housing.monthlyOwnerCostsPercentIncome.latest
                    ).format('$0,0a')
                  : 'N/A',
                numeral(
                  cbsaProperties.housing.monthlyOwnerCostsPercentIncome.latest
                ).format('$0,0a')
                // numeral(
                //   countryProperties.housing.monthlyOwnerCostsPercentIncome
                //     .latest
                // ).format('$0,0a')
              ]
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h6">Summary</Typography>

          <Typography className={classes.summary} variant="subtitle1">
            {'This neighborhood meets '}
            <b>{score > 0.8 ? 'most' : score > 0.5 ? 'some' : 'not a lot'}</b>
            {' of your search criteria:'}

            <ul>
              <li>
                {' The typical age of the population is '}
                <b>{ageRatio < 1 ? 'lower' : 'higher'}</b>
                {' than the average in the surrounding city.'}
              </li>
              <li>
                {' The household income tends to be '}
                <b>{incomeRatio < 1 ? 'less' : 'more'}</b>
                {' than usual.'}
              </li>
              <li>
                {' The homes are usually '}
                <b>{valueRatio < 1 ? 'less' : 'more'}</b>
                {' expensive than comparable areas.'}
              </li>
            </ul>

            {'The overall score for this Lieuucation is a '}
            <b>{scoreString}</b>
            {', given the search criteria you input.'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h6">Housing</Typography>

          <LieuucationTable
            head={['', 'Micromarket', 'City']}
            rows={[
              [
                'Home Price',
                properties.housing.homeValue.latest > 0
                  ? numeral(properties.housing.homeValue.latest).format('$0,0a')
                  : 'N/A',
                numeral(cbsaProperties.housing.homeValue.latest).format('$0,0a')
              ],

              [
                'Rent',
                properties.housing.grossRent.latest > 0
                  ? numeral(properties.housing.grossRent.latest).format('$0,0')
                  : 'N/A',
                numeral(cbsaProperties.housing.grossRent.latest).format('$0,0')
              ],

              [
                '30-Year Mortgage Payment',
                properties.housing.homeValue.latest > 0
                  ? numeral(mortgageCost).format('$0,0')
                  : 'N/A',
                numeral(mortgageCostCbsa).format('$0,0')
              ],

              [
                'Homes Built after 2000',
                numeral(properties.housing.homesBuiltOnOrAfter2000).format(
                  '0%'
                ),
                numeral(cbsaProperties.housing.homesBuiltOnOrAfter2000).format(
                  '0%'
                )
              ]
            ]}
          />
        </Grid>
        {properties.shortTermRentals && cbsaProperties.shortTermRentals && (
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Short Term Rentals</Typography>
            <LieuucationTable
              head={['', 'Micromarket', 'City']}
              rows={[
                properties.shortTermRentals.airbnb.nightly.roomRate <= 0 &&
                properties.shortTermRentals.airbnb.nightly.wholeHomeRate <= 0
                  ? ['No Airbnb listings for this area', '', '']
                  : [],
                properties.shortTermRentals.airbnb.nightly.roomRate > 0
                  ? [
                      'Airbnb Room Rate',
                      numeral(
                        properties.shortTermRentals.airbnb.nightly.roomRate
                      ).format('$0,0'),
                      numeral(
                        cbsaProperties.shortTermRentals.airbnb.nightly.roomRate
                      ).format('$0,0')
                    ]
                  : [],
                properties.shortTermRentals.airbnb.nightly.wholeHomeRate > 0
                  ? [
                      'Airbnb Home Rate',
                      numeral(
                        properties.shortTermRentals.airbnb.nightly.wholeHomeRate
                      ).format('$0,0'),
                      numeral(
                        cbsaProperties.shortTermRentals.airbnb.nightly
                          .wholeHomeRate
                      ).format('$0,0')
                    ]
                  : []
              ]}
            />
          </Grid>
        )}
        {/* {properties.schools && cbsaProperties.schools && (
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Schools</Typography>
            <LieuucationTable
              head={['', 'Micromarket', 'City']}
              rows={[
                !properties.schools.assignedSchoolsRating.overall &&
                !properties.schools.privateSchoolsRating.overall &&
                properties.schools.allSchoolsRating.overall
                  ? [
                      'All Available Schools Rating',
                      numeral(
                        properties.schools.allSchoolsRating.overall
                      ).format('0%'),
                      numeral(
                        cbsaProperties.schools.allSchoolsRating.overall
                      ).format('0%')
                    ]
                  : [],

                properties.schools.assignedSchoolsRating.overall > 0
                  ? [
                      'District Schools Rating',
                      numeral(
                        properties.schools.assignedSchoolsRating.overall
                      ).format('0%'),
                      numeral(
                        cbsaProperties.schools.assignedSchoolsRating.overall
                      ).format('0%')
                    ]
                  : [],
                properties.schools.privateSchoolsRating.overall > 0
                  ? [
                      'Private Schools Rating',
                      numeral(
                        properties.schools.privateSchoolsRating.overall
                      ).format('0%'),
                      numeral(
                        cbsaProperties.schools.privateSchoolsRating.overall
                      ).format('0%')
                    ]
                  : []
              ]}
            />
          </Grid>
        )} */}
        {properties.safety && cbsaProperties.safety && (
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Safety</Typography>
            <LieuucationTable
              head={['', 'Micromarket', 'City']}
              rows={[
                properties.safety.crimes.nonViolent.totalCount >= 0
                  ? [
                      'Nonviolent Crimes in Past Month',
                      numeral(
                        properties.safety.crimes.nonViolent.totalCount
                      ).format('0'),
                      numeral(
                        cbsaProperties.safety.crimes.nonViolent.totalCount
                      ).format('0.0')
                    ]
                  : [],
                properties.safety.crimes.violent.totalCount >= 0
                  ? [
                      'Violent Crimes in Past Month',
                      numeral(
                        properties.safety.crimes.violent.totalCount
                      ).format('0'),
                      numeral(
                        cbsaProperties.safety.crimes.violent.totalCount
                      ).format('0.0')
                    ]
                  : []
              ]}
            />
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default withStyles(styles)(ExecutiveSummary);
